import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEqual, isUndefined } from 'lodash-es';

import { OWNERS_HUB_ROUTES } from '@/constants/routes';
import { resolveConfiguratorFlowBase } from '@/utils/configurator-flow';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  clearPendingBrandSessionId,
  readPendingBrandSessionId,
  resolveInitialBrand,
  setActiveBrand,
  userBrandsList,
} from '@/store/slices/casino-instance';
import {
  clearSessionData,
  sessionCheck,
  sessionLoad,
  selectIsOnboardingInProgress,
  selectStaleUnpaidSession,
} from '@/store/slices/session';

import { Icon } from '../Icon/Icon';
import { Text } from '../Text/Text';
import { ROUTE } from '../../routes';
import { BRAND_SWITCHER, BRAND_SWITCHER_ANIM as ANIM } from '../../theme/tokens';
import {
  Root,
  Logo,
  LogoPlaceholder,
  Trigger,
  Menu,
  BrandList,
  Row,
  RowSelect,
  RowLabel,
  RowActions,
  Name,
  CheckSlot,
  RowActionButton,
  Divider,
  NewBrandButton,
  NewBrandLabel,
} from './BrandSelector.styles';
import type { DefaultItem, EntryDate } from '@/components/modules/spark/configurator/types';

interface Deployment {
  session_id: string;
}

/** Logo placement slots produced by the branding pipeline. */
export type LogoSlot =
  | 'topNavDesktop'
  | 'topNavMobile'
  | 'onboarding'
  | 'footer'
  | 'name_horizontal'
  | 'name_horizontal_black'
  | 'name_horizontal_white'
  | 'short_name'
  | 'symbol'
  | 'symbol_and_name_horizontal'
  | 'symbol_and_name_vertical'
  | 'symbol_and_short_name_horizontal';

export interface LogoAsset {
  src: string;
  alt: string;
}

export interface BrandDetails {
  brand: string;
  domain: string;
  /** Older records hold a single CDN URL; newer ones a per-slot map. */
  logo: string | Partial<Record<LogoSlot, LogoAsset>>;
}

export interface CasinoData {
  details: BrandDetails;
  invoices: { next_invoice_date: string };
}

/** A purchased gamification item on the brand: the configurator selection (`DefaultItem`:
 *  `item_id`, `tier_id` — numeric, or 'BASIC' on the base item — and `type`) plus billing state. */
export interface GamificationItem extends DefaultItem {
  cost: number;
  discounted_cost?: number;
  id: string;
  active: boolean;
  renew: boolean;
  expires_at: string;
}

export interface GamificationData {
  gamification_items: GamificationItem[];
}

export type PipelineStepState = 'active' | 'completed' | 'failed' | 'pending' | 'skipped';

export interface PipelineSteps {
  active: string[];
  pending: string[];
  /** step name → completion unix timestamp (seconds) */
  completed: Record<string, number>;
  failed: Record<string, { error_type: string; failed_at: number }>;
  /** step name → skip reason ('blocked_by_dependencies:<step>', 'unsupported_class', …) */
  skipped: Record<string, string>;
}

export interface PipelineGamificationItem {
  gamification_commercial_class: string;
  gamification_commercial_tier: string;
  step_name: string;
  status: PipelineStepState;
  completed_at_unix_seconds: number | null;
}

export interface LogoVariant {
  key: string;
  src: string;
  alt: string;
  width: number;
  height: number;
  format: string;
  parent_keys: string[];
  generation_method: string;
  /** AI-generation parameters — large and free-form. */
  generation_plan?: Record<string, unknown>;
  prompt?: string | null;
  size?: string | null;
  score?: number | null;
  judgment?: string | null;
}

/** UI surfaces a logo gets placed on. */
export type LogoPlacement = 'topNavDesktop' | 'topNavMobile' | 'onboarding' | 'footer';

export interface LogoAssets {
  original: string;
  logos: Partial<Record<LogoSlot, LogoAsset>>;
  favicon: { src: string; alt: string; size: number; source_asset: string };
  skipped: boolean;
  /** placement → which logo variant fills it */
  selected_slots: Record<LogoPlacement, LogoSlot>;
  variants?: Record<string, LogoVariant>;
  loaderImage?: string;
}

/** Content-deployment pipeline progress; only present while a deployment is running. */
export interface BrandStatusDetails {
  pipeline_id: string;
  tenant_id: number;
  /** 'processing' observed. */
  status: string;
  steps: PipelineSteps;
  /** Empty in all current records; element shape observed only on pre-update data. */
  gamificationItems: PipelineGamificationItem[];
  logoAssets?: LogoAssets;
}

export interface Brand extends EntryDate {
  id: string;
  user_id: string;
  tenant_id: string;
  deployments: Deployment[];
  casino_data: CasinoData;
  gamification_data: GamificationData;
  /** Deployment status; 'content_deployment_in_progress' observed, null when idle. */
  status: string | null;
  status_details: BrandStatusDetails | null;
  log: string | null;
}

export interface ContextBrand extends Brand {
  active: boolean;
  onClick: () => void;
}

export interface BrandsSelectorContextValue {
  brands: ContextBrand[];
}

type BrandIdentity = { name: string; logo: string | null };

// `logo` is a map of named variants ({ symbol, topNavDesktop, … }), each a
// { src, alt } asset; legacy records may hold a bare URL string. For the small
// square switcher slot the `symbol` variant fits best — fall back through the
// other compact variants, then any variant with a `src`. Normalising to a URL
// string here is what stops it rendering as "[object Object]".
const PREFERRED_VARIANTS = ['symbol', 'topNavDesktop', 'topNavMobile', 'footer', 'short_name'] as const;

// Slack (px) the list may overflow before it counts as scrollable. Must clear the
// row reveal's slide distance (6px) plus sub-pixel rounding, so the open cascade's
// transient transforms don't flash a scrollbar on a list that fits.
const SCROLL_OVERFLOW_TOLERANCE_PX = 8;

const getLogoUrl = (logo: BrandDetails['logo'] | undefined): string | null => {
  if (!logo) return null;
  if (typeof logo === 'string') return logo;

  for (const name of PREFERRED_VARIANTS) {
    const src = logo[name]?.src;
    if (src) return src;
  }
  return Object.values(logo).find((variant) => variant?.src)?.src ?? null;
};

// The brand's logo source, hardened against the two shapes the casino-instance API
// ships. The per-slot variant map now lives at `status_details.logoAssets.logos`,
// with `casino_data.details.logo` reduced to the single `original` URL string (a wide
// lockup that reads poorly in this small square slot); older records still carry that
// map inline at `casino_data.details.logo`. Prefer the `logoAssets` map (present and
// identically-shaped across both eras, so `getLogoUrl` can pick the compact `symbol`),
// then fall back to `details.logo` (a legacy inline map, or a bare URL). Mirrored by
// `brandLogoUrl` in `pages/wallet` and `brandLogoSource` in `pages/casino-settings/logos`.
const brandLogoSource = (brand?: Brand): BrandDetails['logo'] | undefined => {
  const variantMap = brand?.status_details?.logoAssets?.logos;
  if (variantMap && Object.keys(variantMap).length > 0) {
    return variantMap;
  }
  return brand?.casino_data?.details?.logo;
};

// Name + logo flattened from the brand record; falls back to the id so a brand still
// mid-setup renders something rather than blank.
const getBrandIdentity = (brand?: Brand): BrandIdentity => {
  const details = brand?.casino_data?.details;
  return { name: details?.brand ?? brand?.id ?? '', logo: getLogoUrl(brandLogoSource(brand)) };
};

// Normalise the brand's stored domain (a bare host or an already-absolute URL) to
// an absolute https URL for the "open live site" action. Mirrors `toHref` in the
// casino-settings DomainsSection.
const toSiteHref = (domain: string): string => (/^https?:\/\//i.test(domain) ? domain : `https://${domain}`);

// Picks the logo tile tone for best contrast by sampling the artwork's average
// luminance (alpha-weighted, so a transparent logo is judged on its ink, not its
// empty space): a light/white mark sits on the dark tile, a dark/black mark on the
// light one — so a brand logo never disappears into its backing. Falls back to the
// dark tile (the near-white initial-letter fallback reads on it) when there's no
// logo or the pixels can't be read — a cross-origin CDN asset whose canvas is
// tainted (no CORS) — so it degrades safely. Same model as the "Website logos"
// preview in brand settings (casino-settings LogosSection `useAutoFrame`).
const useLogoFrame = (src: string | null): 'light' | 'dark' => {
  const [frame, setFrame] = React.useState<'light' | 'dark'>('dark');
  React.useEffect(() => {
    setFrame('dark');
    if (!src) {
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      try {
        const size = 24;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        let lum = 0;
        let alpha = 0;
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3];
          lum += (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) * a;
          alpha += a;
        }
        if (alpha === 0) return;
        setFrame(lum / alpha > 140 ? 'dark' : 'light');
      } catch {
        // Tainted canvas (no CORS) — keep the fallback tone.
      }
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src]);
  return frame;
};

// Renders a brand logo on a contrast tile whose tone reacts to the logo (see
// `useLogoFrame`), so the same component serves both the collapsed top-bar trigger
// and the dropdown rows and the mark stays legible on either.
//
// Fallback order: logo image → brand initial → neutral skeleton placeholder. A
// real brand always has a name (it falls back to its id in `getBrandIdentity`),
// so an empty name means there is NO active brand yet — loading, or a stale/empty
// session. We render the pulsing placeholder rather than a literal "?" so that
// transient/empty states never surface a broken-looking glyph in the top bar.
const BrandLogo = ({ identity }: { identity: BrandIdentity }): React.ReactElement => {
  const frame = useLogoFrame(identity.logo);
  return (
    <Logo aria-hidden="true" $frame={frame}>
      {identity.logo ? (
        <img src={identity.logo} alt="" />
      ) : identity.name ? (
        <Text text={identity.name.charAt(0)} tag="span" />
      ) : (
        <LogoPlaceholder />
      )}
    </Logo>
  );
};

export type BrandSelectorProps = {
  /** Raw CSS handed down by the config layout's ModuleElement. */
  styleText?: string;
  className?: string;
};

/**
 * Top-bar brand switcher. Lives in the shared component set (it's reusable
 * presentational chrome) but is mounted by the config-driven layout via
 * `ModuleElement('oh.module.brands-selector')`. Reads brands from the
 * `casinoInstance` slice; the trigger shows the active brand's logo, the
 * dropdown lists all brands (logo + name + active check) plus "New brand".
 */
export const BrandSelector = ({ styleText = '', className = '' }: BrandSelectorProps): React.ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const brands = useAppSelector((state) => state.casinoInstance.brands, isEqual);
  const activeBrand = useAppSelector((state) => state.casinoInstance.activeBrand, isEqual);
  // A half-finished, unpaid configurator session (if any). When present, "New brand"
  // resumes it rather than starting a fresh one — you can only build one website at
  // a time, so the owner is sent back to the one they left.
  const staleSession = useAppSelector(selectStaleUnpaidSession, isEqual);
  // A PAID brand that is still being set up (not yet live). Same "one build at a time"
  // rule: the footer offers "Continue setup" (→ onboarding) instead of "New brand", and
  // the configurator route itself is off-limits (it redirects home).
  const onboardingInProgress = useAppSelector(selectIsOnboardingInProgress);
  const sessionLoaded = useAppSelector((state) => state.session.loaded);
  const sessionLoading = useAppSelector((state) => state.session.loading);

  const [open, setOpen] = React.useState(false);
  // Two-phase mount so the dropdown animates in and out.
  const [mounted, setMounted] = React.useState(false);
  const [entered, setEntered] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const brandListRef = React.useRef<HTMLDivElement>(null);
  // Whether the brand list overflows, and past which edge — drives the scroll
  // fade so a row dissolves into the panel chrome instead of being hard-cut. All
  // false when everything fits (no scrollbar, no fade, active row stays crisp).
  const [fade, setFade] = React.useState<{ scrollable: boolean; top: boolean; bottom: boolean }>({
    scrollable: false,
    top: false,
    bottom: false,
  });

  const updateFade = React.useCallback(() => {
    const el = brandListRef.current;
    if (!el) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    // The open cascade slides each row up from a few px below its rest spot; that
    // transient transform counts toward scroll overflow and would flash a
    // scrollbar on a list that actually fits. Treat the list as scrollable only
    // when it overflows by more than that slide (+ sub-pixel rounding).
    const scrollable = scrollHeight - clientHeight > SCROLL_OVERFLOW_TOLERANCE_PX;
    const top = scrollable && scrollTop > 1;
    const bottom = scrollable && scrollTop < scrollHeight - clientHeight - 1;
    setFade((prev) =>
      prev.scrollable === scrollable && prev.top === top && prev.bottom === bottom
        ? prev
        : { scrollable, top, bottom },
    );
  }, []);
  // The panel is portaled to `document.body` to escape the layout chrome's
  // `overflow: hidden`, so it's positioned `fixed` from the trigger's viewport
  // rect. `-8px` mirrors the old `top:-8 / left:-8` anchor that overlays the
  // first row's logo onto the collapsed trigger logo.
  const [menuPos, setMenuPos] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const updateMenuPos = React.useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuPos({ top: rect.top - 8, left: rect.left - 8 });
    }
  }, []);

  React.useEffect(() => {
    dispatch(userBrandsList());
  }, [dispatch]);

  // Learn the active session's status wherever the switcher renders (not just on Home),
  // so the footer can tell a paid-but-not-yet-live build (→ "Continue setup") from a clean
  // slate (→ "New brand"). One-shot; `ambient: true` so this read never adopts a dead
  // orphan (a stuck `payment_successful`, or an id cleared by "New brand" mid-load) — that
  // resurrection is what bounced "New brand" back to Home. Never mints (no id → no-op).
  const sessionRequested = React.useRef(false);
  React.useEffect(() => {
    if (sessionRequested.current) return;
    sessionRequested.current = true;
    if (!sessionLoaded && !sessionLoading) dispatch(sessionLoad({ ambient: true }));
  }, [dispatch, sessionLoaded, sessionLoading]);

  // Re-probe for a stale, unpaid session each time the switcher opens, so "New brand"
  // reflects the latest state (idempotent: `/check` never mints a session).
  React.useEffect(() => {
    if (open) dispatch(sessionCheck());
  }, [open, dispatch]);

  // Decide which brand the switcher shows. Two cases:
  //  1. A brand just created via the deploy flow — its deploy session was stashed
  //     as "pending" at deploy time — wins the moment it appears in the list,
  //     even over a currently-active brand. That's "select the brand I just set
  //     up and deployed".
  //  2. Otherwise, only when nothing is selected yet (e.g. right after a reload,
  //     since the choice lives in Redux which resets): restore the persisted
  //     choice, falling back to the first brand.
  React.useEffect(() => {
    if (brands.length === 0) {
      return;
    }

    const pendingSessionId = readPendingBrandSessionId();
    if (pendingSessionId) {
      const pendingBrand = brands.find((brand) =>
        brand.deployments?.some((deployment) => deployment.session_id === pendingSessionId),
      );
      if (pendingBrand) {
        // It's provisioned now — adopt it and drop the marker.
        clearPendingBrandSessionId();
        if (pendingBrand.id !== activeBrand?.id) {
          dispatch(setActiveBrand(pendingBrand));
        }
        return;
      }
      // Not provisioned yet — leave the marker; the next brands refetch (e.g. on
      // the next mount) will pick it up.
    }

    if (isUndefined(activeBrand)) {
      dispatch(setActiveBrand(resolveInitialBrand(brands) ?? brands[0]));
    }
  }, [dispatch, brands, activeBrand]);

  React.useEffect(() => {
    if (open) {
      setMounted(true);
      // Double rAF: let the opacity-0 frame paint before flipping to `entered`,
      // otherwise the browser coalesces 0→1 and the open transition never runs.
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setEntered(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    setEntered(false);
    const timeout = window.setTimeout(() => setMounted(false), ANIM.EXIT_MS);
    return () => window.clearTimeout(timeout);
  }, [open]);

  // Keep the portaled panel pinned to the trigger: measure before paint on open,
  // then follow viewport changes (scroll in any ancestor — hence capture — and
  // resize) while it's open.
  React.useLayoutEffect(() => {
    if (!open) {
      return;
    }
    updateMenuPos();
    window.addEventListener('scroll', updateMenuPos, true);
    window.addEventListener('resize', updateMenuPos);
    return () => {
      window.removeEventListener('scroll', updateMenuPos, true);
      window.removeEventListener('resize', updateMenuPos);
    };
  }, [open, updateMenuPos]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // The panel now lives in a portal outside `rootRef`, so check it too —
      // otherwise a click inside the dropdown reads as "outside" and closes it.
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const items = React.useMemo(
    () =>
      brands
        .map((brand) => ({
          brand,
          identity: getBrandIdentity(brand),
          active: !isUndefined(activeBrand) ? brand.id === activeBrand.id : false,
        }))
        .sort((a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1)),
    [brands, activeBrand],
  );

  const activeIdentity = getBrandIdentity(activeBrand);

  // Recompute the scroll fade once the list is laid out and whenever its content
  // or the available height changes (the menu's max-height tracks the viewport).
  // Transforms from the row reveal don't affect scroll metrics, so measuring
  // mid-animation is accurate.
  React.useLayoutEffect(() => {
    if (!mounted) {
      return;
    }
    updateFade();
    window.addEventListener('resize', updateFade);
    return () => window.removeEventListener('resize', updateFade);
  }, [mounted, items.length, menuPos.top, updateFade]);

  // Delay for the tail elements (divider, then "New brand"): one stagger step
  // past the last brand row so they close the cascade.
  const tailDelay = ANIM.ROWS_DELAY_MS + Math.max(0, items.length - 1) * ANIM.STAGGER_MS;

  const onSelect = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset.id;
      const next = brands.find((brand) => brand.id === id);
      if (next) {
        dispatch(setActiveBrand(next));
      }
      setOpen(false);
    },
    [brands, dispatch],
  );

  // The cog on the active row jumps straight to its Website Settings. It's a
  // sibling of the select button, so it won't bubble into `onSelect`; the
  // `stopPropagation` is belt-and-braces against any future restructure.
  const onOpenSettings = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setOpen(false);
      navigate(ROUTE.settingsSection('website-settings'));
    },
    [navigate],
  );

  // The active brand's live web address, if it has one (a brand still mid-setup
  // has no domain yet, so the button below is gated on this and never opens a
  // dead URL). Read from the active brand rather than the row so the handler is a
  // single stable callback instead of one closure per row.
  const activeDomain = activeBrand?.casino_data?.details?.domain?.trim() ?? '';

  // The globe/arrow button on the active row opens that brand's live site in a new
  // tab. Sibling of the select button, so it won't bubble into `onSelect`; the
  // `stopPropagation` guards against any future restructure. `noopener,noreferrer`
  // severs the opener handle, matching the logo pop-out in casino-settings.
  const onOpenSite = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (!activeDomain) return;
      setOpen(false);
      window.open(toSiteHref(activeDomain), '_blank', 'noopener,noreferrer');
    },
    [activeDomain],
  );

  const onAddBrand = React.useCallback(async () => {
    setOpen(false);
    // A paid brand is still being set up (not yet live) — you can only build one website
    // at a time, so this footer action is "finish that one", not a new brand: send the
    // owner to onboarding (the customizer) rather than the configurator.
    if (onboardingInProgress) {
      navigate(OWNERS_HUB_ROUTES.STUDIO_CONFIGURATOR);
      return;
    }
    // Resume the half-finished, unpaid session if there is one: load it into the
    // active configurator session FIRST so the wizard reopens that build instead of
    // starting (or resuming) a different one.
    if (staleSession) {
      try {
        await dispatch(sessionLoad({ id: staleSession.id })).unwrap();
      } catch {
        // Fall through — the configurator retries the persisted id on mount.
      }
    } else {
      // No unpaid build to resume — this is a genuine "New brand". The previous
      // build's session lingers in the store + localStorage after a completed flow
      // (the nova onboarding/deploy flow never tears it down, unlike the old v2
      // hooks), and it is PAST `configuration` (paid/done) so it isn't a stale-unpaid
      // session either. Without clearing it, the configurator's bootstrap sees a
      // loaded session and skips `sessionEnsure()`, reopening that dead paid build:
      // the old design is pre-selected, the pack chooser is degraded, and checkout
      // 404s as "session expired". Discard it so the bootstrap mints a fresh one.
      dispatch(clearSessionData());
    }
    // Enter the configurator for the resolved flow (v3 by default; a `startProperties.flow`
    // hand-off, remembered across the session, can steer resume into an alternate flow).
    navigate(resolveConfiguratorFlowBase());
  }, [dispatch, navigate, staleSession, onboardingInProgress]);

  return (
    <Root ref={rootRef} className={className} $styleText={styleText}>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <BrandLogo identity={activeIdentity} />
        <Icon name="chevron-expand" size={16} color={BRAND_SWITCHER.chevron} />
      </Trigger>

      {mounted
        ? createPortal(
            <Menu ref={menuRef} role="listbox" $entered={entered} $top={menuPos.top} $left={menuPos.left}>
              <BrandList
                ref={brandListRef}
                onScroll={updateFade}
                $scrollable={fade.scrollable}
                $fadeTop={fade.top}
                $fadeBottom={fade.bottom}
              >
                {items.map(({ brand, identity, active }, index) => {
                  const lead = index === 0;
                  // Lead row's name/check follow the background; later rows cascade.
                  const rowDelay = lead ? ANIM.NAME_DELAY_MS : ANIM.ROWS_DELAY_MS + (index - 1) * ANIM.STAGGER_MS;
                  return (
                    <Row key={brand.id} $entered={entered} $lead={lead} $delayMs={rowDelay}>
                      <RowSelect
                        type="button"
                        role="option"
                        aria-selected={active}
                        data-id={brand.id}
                        onClick={onSelect}
                      >
                        <RowLabel>
                          <BrandLogo identity={identity} />
                          <Name
                            $active={active}
                            $animate={lead}
                            $entered={entered}
                            $delayMs={ANIM.NAME_DELAY_MS}
                            text={identity.name}
                            tag="span"
                          />
                        </RowLabel>
                      </RowSelect>
                      <RowActions>
                        <CheckSlot $active={active} $entered={entered} $delayMs={ANIM.NAME_DELAY_MS}>
                          <Icon name="check" size={16} color={BRAND_SWITCHER.accent} />
                        </CheckSlot>
                        {active && activeDomain ? (
                          <RowActionButton
                            type="button"
                            aria-label={t('Open {{name}} in a new tab', { name: activeDomain })}
                            onClick={onOpenSite}
                            $entered={entered}
                            $delayMs={ANIM.NAME_DELAY_MS}
                          >
                            <Icon name="external-link" size={16} />
                          </RowActionButton>
                        ) : null}
                        {active ? (
                          <RowActionButton
                            type="button"
                            aria-label={t('Open website settings')}
                            onClick={onOpenSettings}
                            $entered={entered}
                            $delayMs={ANIM.NAME_DELAY_MS}
                          >
                            <Icon name="settings" size={16} />
                          </RowActionButton>
                        ) : null}
                      </RowActions>
                    </Row>
                  );
                })}
              </BrandList>

              <Divider $entered={entered} $delayMs={tailDelay} />

              <NewBrandButton
                type="button"
                onClick={onAddBrand}
                $entered={entered}
                $delayMs={tailDelay + ANIM.STAGGER_MS}
              >
                {onboardingInProgress || staleSession ? (
                  <>
                    <Icon name="arrow-right-circle" size={20} color={BRAND_SWITCHER.onAccent} />
                    <NewBrandLabel text="Continue setup" tag="span" />
                  </>
                ) : (
                  <>
                    <Icon name="plus-circle" size={20} color={BRAND_SWITCHER.onAccent} />
                    <NewBrandLabel text="New brand" tag="span" />
                  </>
                )}
              </NewBrandButton>
            </Menu>,
            document.body,
          )
        : null}
    </Root>
  );
};

export default BrandSelector;
